import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useState,
} from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HouseConsignmentAdditionalSupplyChainActors } from 'features/declaration/form/components/AdditionalSupplyChainActors'
import { HouseConsignmentFormValues } from 'features/declaration/house-consignment/models/schema'
import DepartureTransportMeans from 'features/declaration/form/components/DepartureTransportMeans'
import SupportingDocuments from 'features/declaration/form/components/SupportingDocuments'
import TransportDocuments from 'features/declaration/form/components/TransportDocuments'
import {
  blankItemDepartureTransportMeans,
  blankSupportingDocument,
  blankTransportDocument,
} from 'features/declaration/models/blankItems'
import { HandleSubmitResult } from 'features/declaration/form/components/TransitFormTabHandle'
import useHouseConsignment from '../../../../house-consignment/components/useHouseConsignment'
import { toHouseConsignmentFormValues } from '../../../../house-consignment/models/mapper'
import PlaceOfAcceptance from '../../../../house-consignment/components/PlaceOfAcceptance'
import PlaceOfDelivery from '../../../../house-consignment/components/PlaceOfDelivery'
import Buyer from '../../../../house-consignment/components/Buyer'
import Seller from '../../../../house-consignment/components/Seller'
import CollapsibleFormGroup from '../../../../../../components/p5/CollapsibleFormGroup'
import CountriesOfRouting from '../../../../house-consignment/components/CountriesOfRouting'
import PassiveBorderTransportMeans from '../../../../house-consignment/components/PassiveBorderTransportMeans'
import TransportEquipment from '../../../../house-consignment/components/TransportEquipment'
import useDeclarationCountry from '../../../components/useDeclarationCountry'
import { getErrorMessage } from '../../../../../../errors/ApiError'
import EnsHouseConsignmentInfo from './EnsHouseConsignmentInfo'
import {
  HouseConsignmentFormHandle,
  HouseConsignmentRowData,
} from '../../../../house-consignment/components/types'
import useHouseConsignmentForm, { HouseConsignmentFormSaveResult } from '../../../../house-consignment/components/useHouseConsignmentForm'
import { useDeclarationEditorContext } from '../../../contexts/DeclarationEditorContext'
import LoadingOverlay from '../../../../../../components/p5/LoadingOverlay'
import useScopedServerRequirements from '../../../validations/useScopedServerRequirements'
import useNctsPhase from '../../../components/useNctsPhase'
import { notifyHouseConsignmentSaveResult } from '../../../../house-consignment/components/notifyHouseConsignmentSaveResult'

interface HouseConsignmentFormProps {
  rowData: HouseConsignmentRowData
  onSaveSuccess?: () => void
}

const EnsHouseConsignmentForm = forwardRef<
HouseConsignmentFormHandle,
HouseConsignmentFormProps
>(({
  rowData,
  onSaveSuccess,
}, ref) => {
  const { declarationData, editorData } = useDeclarationEditorContext()
  const { consignment, transitOperation } = declarationData
  if (!transitOperation?.id) throw new Error('Missing transit operation')
  if (!consignment?.id) throw new Error('Missing consignment')

  const { phase, isOptIn } = useNctsPhase()
  const { t } = useTranslation()
  const [isSaving, setIsSaving] = useState(false)
  const [isFormPopulated, setIsFormPopulated] = useState(false)
  const [isCleanBaselineSet, setIsCleanBaselineSet] = useState(false)

  const { id } = rowData

  const consignmentId = consignment.id
  const transitOperationId = transitOperation.id

  const {
    houseConsignment, houseConsignmentDocuments, isLoading, saveHouseConsignmentFormValues,
  } = useHouseConsignment(id, transitOperationId, consignmentId)

  const { form } = useHouseConsignmentForm()
  const {
    reset, handleSubmit, formState: { isDirty },
  } = form

  const { isLoading: isServerRequirementsLoading } = useScopedServerRequirements('houseConsignment', {
    transitOperationId,
    phase,
    isOptIn,
    houseConsignmentId: id,
  }, { enabled: isFormPopulated })

  useEffect(() => {
    if (isFormPopulated) return

    if (!houseConsignment || !houseConsignmentDocuments) return
    reset(toHouseConsignmentFormValues(houseConsignment, houseConsignmentDocuments))
    setIsFormPopulated(true)
  }, [reset, houseConsignment, houseConsignmentDocuments, isFormPopulated])

  useEffect(() => {
    if (isCleanBaselineSet || !isFormPopulated || isLoading || isServerRequirementsLoading) return
    reset(form.getValues())
    setIsCleanBaselineSet(true)
  }, [isCleanBaselineSet, isFormPopulated, isLoading, isServerRequirementsLoading, reset, form])

  const inlandModeOfTransport = editorData?.inlandModeOfTransport ?? ''
  const borderModeOfTransport = editorData?.borderModeOfTransport ?? ''
  const country = useDeclarationCountry()

  const validateAndGet = useCallback(() => new Promise<HandleSubmitResult<HouseConsignmentFormValues>>((resolve) => {
    handleSubmit(
      (data) => {
        resolve({ success: true, data })
      },
      (errors) => {
        resolve({ success: false, errors })
      },
    )()
  }), [handleSubmit])

  const save = useCallback(async (): Promise<HouseConsignmentFormSaveResult> => {
    setIsSaving(true)
    try {
      const result = await validateAndGet()
      if (!result.success) {
        return { success: false, errorReason: 'validation' }
      }
      if (!result.data) {
        return { success: false, errorReason: 'general', errors: [{ message: 'Data missing' }] }
      }

      const { data: saved, errors } = await saveHouseConsignmentFormValues({
        values: result.data,
        isDraft: true,
        standalone: true,
      })

      if (errors.length) {
        return { success: false, errorReason: 'general', errors }
      }

      // Rebase the form on the just-saved server state.
      form.reset(toHouseConsignmentFormValues(saved.houseConsignment, saved.houseConsignmentDocuments))

      onSaveSuccess?.()
      return { success: true, data: saved }
    } catch (err) {
      return { success: false, errorReason: 'unexpected', errors: [{ message: getErrorMessage(err) }] }
    } finally {
      setIsSaving(false)
    }
  }, [form, onSaveSuccess, saveHouseConsignmentFormValues, validateAndGet])

  useImperativeHandle(ref, () => ({
    save,
    isDirty: () => isDirty,
  }))

  const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    const result = await save()
    notifyHouseConsignmentSaveResult(result, t)
  }

  return (
    <LoadingOverlay loading={isLoading || isServerRequirementsLoading || isSaving}>
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <div className="hc-form">
            {/* General house consignment info (3 fields) on the first row. */}
            <div className="hc-form__thirds">
              <EnsHouseConsignmentInfo />
            </div>
            <CollapsibleFormGroup title={t('declaration.p5.houseConsignmentDrawer.sellerAndBuyer')}>
              <div className="row">
                <div className="col-lg-4">
                  <Seller />
                </div>
                <div className="col-lg-4">
                  <Buyer />
                </div>
                <div className="col-lg-4">
                  <HouseConsignmentAdditionalSupplyChainActors />
                </div>
              </div>
            </CollapsibleFormGroup>

            <CollapsibleFormGroup title={t('declaration.p5.houseConsignmentDrawer.documents')}>
              <div className="row">
                <div className="col-lg-6">
                  <TransportDocuments<HouseConsignmentFormValues>
                    scope="houseConsignment"
                    createDefault={() => blankTransportDocument}
                    country={country}
                    isEns
                  />
                </div>
                <div className="col-lg-6">
                  <SupportingDocuments<HouseConsignmentFormValues>
                    scope="houseConsignment"
                    createDefault={() => blankSupportingDocument}
                    isEns
                    country={country}
                  />
                </div>
              </div>
            </CollapsibleFormGroup>
            <CollapsibleFormGroup title={t('declaration.p5.houseConsignmentDrawer.transportation')}>
              <div className="row">
                <div className="col-lg-4">
                  <DepartureTransportMeans<HouseConsignmentFormValues>
                    name="departureTransportMeans"
                    createDefault={() => blankItemDepartureTransportMeans}
                    inlandModeOfTransport={inlandModeOfTransport}
                  />
                </div>
                <div className="col-lg-4">
                  <PlaceOfAcceptance />
                  <PlaceOfDelivery />
                </div>
                <div className="col-lg-4">
                  <CountriesOfRouting />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <PassiveBorderTransportMeans borderModeOfTransport={borderModeOfTransport} entriesGrid />
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <TransportEquipment />
                </div>
              </div>
            </CollapsibleFormGroup>
          </div>
        </form>
      </FormProvider>
    </LoadingOverlay>
  )
})

EnsHouseConsignmentForm.defaultProps = {
  onSaveSuccess: undefined,
}

export default EnsHouseConsignmentForm
